import { useState } from "react";
import { useTheme, useMediaQuery, Box,  
  Typography, Button, IconButton, 
  Stack, Chip, Paper, Menu, MenuItem 
} from "@mui/material";
import { MoreHoriz, Add } from "@mui/icons-material";
import ConfirmDialog from 'components/ConfirmDialog';
import { useKanban } from "./kanban-provider";
import TaskCard from "./task-card";
import TaskModal from "./task-modal";
import ColumnModal from "./column-modal";


const colorMap = {
  "bg-blue-500": "#3b82f6",
  "bg-green-500": "#22c55e",
  "bg-yellow-500": "#eab308",
  "bg-red-500": "#ef4444",
  "bg-purple-500": "#8b5cf6",
  "bg-pink-500": "#ec4899",
  "bg-indigo-500": "#6366f1",
  "bg-orange-500": "#f97316",
};


export default function KanbanBoard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { state, moveTask, deleteColumn } = useKanban();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState(null);
  
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverColumnId, setDragOverColumnId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);

  const {
    isTaskModalOpen,
    setIsTaskModalOpen,
    isColumnModalOpen,
    setIsColumnModalOpen,
    editingTask,
    setEditingTask,
    editingColumn,
    setEditingColumn,
  } = useKanban();

  const handleDragStart = (task) => {
    setDraggedTask(task);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    setDragOverColumnId(columnId);
  };

  const handleDrop = (e, columnId) => {
    e.preventDefault();
    if (!draggedTask) return;
    if (draggedTask.status !== columnId) {
      moveTask(draggedTask._id, draggedTask.status, columnId);
      console.log(`Task ${draggedTask._id} moved from ${draggedTask.status} to ${columnId}`);
    }
    setDraggedTask(null);
    setDragOverColumnId(null);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDragOverColumnId(null);
  };

  const openEditTaskModal = (task) => {
    setEditingTask(task);
    setIsTaskModalOpen(true);
  };

  const closeTaskModal = () => {
    setIsTaskModalOpen(false);
    setEditingTask(null);
  };

  const openCreateColumnModal = () => {
    setEditingColumn(null);
    setIsColumnModalOpen(true);
  };

  const openEditColumnModal = (column) => {
    setEditingColumn(column);
    setIsColumnModalOpen(true);
  };

  const closeColumnModal = () => {
    setIsColumnModalOpen(false);
    setEditingColumn(null);
  };

  const handleDeleteColumn = (columnId) => {
    const tasksInColumn = state.tasks.filter(
      (task) => task.status === columnId
    ).length;

    if (tasksInColumn > 0) {
      setColumnToDelete(columnId);
      setConfirmDialogOpen(true);
    } else {
      deleteColumn(columnId);
    }
  };

  const confirmDeleteColumn = () => {
    if (!columnToDelete) return;

    deleteColumn(columnToDelete);
    setColumnToDelete(null);
  };


  const handleMenuOpen = (event, column) => {
    setAnchorEl(event.currentTarget);
    setSelectedColumn(column);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedColumn(null);
  };

  const minWidth = 260;
  const maxWidth = 280;
  const minHeight = 160;
  

  return (
    <Box 
      display="flex" 
      flexDirection={isMobile ? "column" : "row"}
      gap={2}      
    >
      {state.columns.map((column) => (
        <Paper
          key={column.id}
          elevation={2}
          sx={{
            p: 1,
            bgcolor: "background.paper",
            width: isMobile ? "100%" : "auto",
            minWidth: isMobile ? "100%" : minWidth,
            maxWidth: isMobile ? "100%" : maxWidth,
            border: dragOverColumnId === column.id ? "2px solid" : "1px solid",
            borderColor: dragOverColumnId === column.id ? "secondary.main" : "divider",
            transition: "all 0.2s",
          }}
          onDragOver={(e) => handleDragOver(e, column.id)}
          onDrop={(e) => handleDrop(e, column.id)}
          data-column-id={column.id}
        >
          <Stack 
            direction="row" 
            alignItems="center" 
            justifyContent="space-between" 
            sx={{ mt: 1 }}
          >
            <Stack 
              direction="row" 
              alignItems="center" 
              spacing={1}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: colorMap[column.color] || "#3b82f6",
                }}
              />
              <Typography variant="subtitle1" fontWeight="medium">
                {column.title}
              </Typography>
              <Chip
                label={state.tasks.filter((task) => task.status === column.id).length}
                size="small"
                sx={{
                  bgcolor: colorMap[column.color] || "#3b82f6",
                  fontSize: "0.75rem",
                  color: "white",
                }}
              />
            </Stack>
            <IconButton size="small" onClick={(e) => handleMenuOpen(e, column)}>
              <MoreHoriz />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl) && selectedColumn?.id === column.id}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  openEditColumnModal(column);
                  handleMenuClose();
                }}
              >
                Edit Column
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleDeleteColumn(column.id);
                  handleMenuClose();
                }}
                sx={{ color: "error.main" }}
              >
                Delete Column
              </MenuItem>
            </Menu>
          </Stack>

          <Box
            sx={{                
              display: "grid", 
              gap: 1,
              minHeight,
              p: 1,
              borderRadius: 1,
              bgcolor: dragOverColumnId === column.id ? "action.hover" : "transparent",
              border: dragOverColumnId === column.id ? "1px dashed" : "none",
              borderColor: dragOverColumnId === column.id ? "primary.main" : "transparent",
              transition: "all 0.2s",
            }}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDrop={(e) => handleDrop(e, column.id)}
            data-column-id={column.id}
          >
            {state.tasks
              .filter((task) => task.status === column.id)
              .map((task) => (
                <TaskCard                    
                  key={task._id}
                  task={task}
                  onEdit={() => openEditTaskModal(task)}
                  isDragging={draggedTask?._id === task._id}
                  onDragStart={() => handleDragStart(task)}
                  onDragEnd={handleDragEnd}
                />
              ))}

            {state.tasks.filter((task) => task.status === column.id).length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: minHeight,
                  border: "1px dashed",
                  borderColor: dragOverColumnId === column.id ? "primary.main" : "grey.300",
                  bgcolor: dragOverColumnId === column.id ? "action.hover" : "transparent",
                  borderRadius: 1,
                }}
                onDragOver={(e) => handleDragOver(e, column.id)}
                onDrop={(e) => handleDrop(e, column.id)}
                data-column-id={column.id}
                data-empty-column="true"
              >
                <Typography variant="body2" color="text.secondary">
                  Drop task here
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      ))}

      <Paper
        elevation={0}        
        sx={{
          p: 2,
          width: isMobile ? "100%" : "auto",
          minWidth: isMobile ? "100%" : minWidth,
          maxWidth: isMobile ? "100%" : maxWidth,
          flexShrink: isMobile ? 1 : 0,
          minHeight,
          border: "1px dashed",
          borderColor: "grey.300",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Button
          variant="text"
          startIcon={<Add sx={{ width: 24, height: 24 }} />}
          onClick={openCreateColumnModal}
          sx={{ flexDirection: "column", p: 3, height: "auto" }}
        >
          Add Column
        </Button>
      </Paper>

      <TaskModal 
        isOpen={isTaskModalOpen} 
        onClose={closeTaskModal} 
        task={editingTask} 
      />
      <ColumnModal 
        isOpen={isColumnModalOpen} 
        onClose={closeColumnModal} 
        column={editingColumn} 
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmDeleteColumn}
        title="Delete Column"
        message="This column contains tasks. All tasks inside will be permanently deleted. Are you sure?"
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
}